import { useQuery } from "@tanstack/react-query";
import { setTenantDateFormat } from "@freyr/freyr-react-common";
import { getTenantSettings } from "../api/admin";
import { useCookieData } from "./useCookieData";
import { useTenantSettingsStore } from "../store/tenantSettingsStore";

type UseTenantSettingsLoaderOptions = {
  /** When true, the caller allows fetching tenant settings if the store is empty. */
  enabled?: boolean;
};

/**
 * Load tenant admin date/time settings into app store and common package.
 * - Root layout: `useTenantSettingsLoader({ enabled: !!tenantId })` on page entry (main grid dates).
 * - On-demand: e.g. HistoryVersionDialog `{ enabled: open }` when store is still empty.
 */
export function useTenantSettingsLoader(
  options?: UseTenantSettingsLoaderOptions,
) {
  const { tenantId } = useCookieData();
  const updateTenantSettings = useTenantSettingsStore(
    (state) => state.updateTenantSettings,
  );
  const hasTenantSettings = useTenantSettingsStore((state) =>
    Boolean(state.dateFormat && state.timeFormat),
  );

  return useQuery({
    queryKey: ["tenantSettings", tenantId],
    queryFn: () => getTenantSettings(tenantId!),
    enabled: Boolean(
      tenantId && (options?.enabled ?? false) && !hasTenantSettings,
    ),
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
    select: (response) => response?.data,
    onSuccess: (data) => {
      if (data) {
        updateTenantSettings({
          dateFormat: data.dateFormat,
          timeFormat: data.timeFormat,
          timeZone: data.timeZone,
        });
        setTenantDateFormat(data.dateFormat);
      }
    },
  });
}
